import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ChatsCircle, PaperPlaneTilt, ImageSquare, Link as LinkIcon, X, DotsThree,
  Flag, Trash, CaretDown, MagnifyingGlass, Sparkle, Translate, ArrowUUpLeft, Bell, BellRinging, PushPin, SealCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const LANGS = ["English", "Hindi", "Malayalam", "Tamil", "Telugu", "Kannada", "Bengali",
  "Marathi", "Gujarati", "Punjabi", "Urdu", "Odia", "Assamese", "Arabic", "French",
  "Spanish", "Portuguese", "German", "Chinese (Simplified)", "Japanese", "Russian"];

function TranslateMenu({ onPick, busy, label = "Translate" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-1 rounded-full border border-border/60 px-3 py-1 text-sm hover:bg-accent disabled:opacity-60" disabled={busy} data-testid="talk-translate-btn">
          <Translate size={15} /> {busy ? "Translating…" : label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l} onClick={() => onPick(l)} data-testid={`talk-translate-${l}`}>{l}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const REACTIONS = [
  { key: "like", e: "👍", label: "Like" },
  { key: "love", e: "❤️", label: "Love" },
  { key: "wow", e: "😮", label: "Wow" },
  { key: "care", e: "🤗", label: "Care" },
  { key: "celebrate", e: "👏", label: "Celebrate" },
  { key: "helpful", e: "💡", label: "Helpful" },
  { key: "respect", e: "🙏", label: "Respect" },
];
const EMOJI = Object.fromEntries(REACTIONS.map((r) => [r.key, r.e]));
const CATS = [
  { k: "all", label: "All" }, { k: "memory", label: "Memories" }, { k: "question", label: "Questions" },
  { k: "find_colleague", label: "Find Colleague" }, { k: "reunion", label: "Reunion" },
  { k: "event", label: "Events" }, { k: "photo", label: "Photos" }, { k: "career", label: "Career" },
  { k: "help", label: "Help" }, { k: "announcement", label: "News" },
];
const CAT_LABEL = Object.fromEntries(CATS.map((c) => [c.k, c.label]));

const ACCENTS = {
  institution: { ring: "ring-emerald-500/20", bar: "bg-emerald-600", soft: "bg-emerald-50", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-800", tab: "bg-emerald-600 text-white", border: "border-emerald-200" },
  alumni: { ring: "ring-rose-500/20", bar: "bg-rose-600", soft: "bg-rose-50", text: "text-rose-700", chip: "bg-rose-100 text-rose-800", tab: "bg-rose-600 text-white", border: "border-rose-200" },
  geography: { ring: "ring-sky-500/20", bar: "bg-sky-600", soft: "bg-sky-50", text: "text-sky-700", chip: "bg-sky-100 text-sky-800", tab: "bg-sky-600 text-white", border: "border-sky-200" },
  article: { ring: "ring-violet-500/20", bar: "bg-violet-600", soft: "bg-violet-50", text: "text-violet-700", chip: "bg-violet-100 text-violet-800", tab: "bg-violet-600 text-white", border: "border-violet-200" },
  event: { ring: "ring-amber-500/20", bar: "bg-amber-600", soft: "bg-amber-50", text: "text-amber-700", chip: "bg-amber-100 text-amber-800", tab: "bg-amber-600 text-white", border: "border-amber-200" },
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
};

function useUpload() {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    const ids = [];
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.id);
      }
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
    return ids;
  };
  return { upload, busy };
}

function Reactions({ reactions, mine, onReact, accent }) {
  const [open, setOpen] = useState(false);
  const total = Object.values(reactions || {}).reduce((a, b) => a + b, 0);
  return (
    <div className="relative flex items-center gap-2">
      <button
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        onClick={() => onReact(mine || "like")}
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors ${mine ? `${accent.chip} ${accent.border}` : "border-border/60 hover:bg-accent"}`}
        data-testid="talk-react-btn">
        <span>{mine ? EMOJI[mine] : "👍"}</span>
        <span className="text-xs font-medium">{mine ? REACTIONS.find((r) => r.key === mine)?.label : "React"}</span>
      </button>
      {open && (
        <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className="absolute bottom-full left-0 z-20 mb-1 flex gap-1 rounded-full border bg-background p-1 shadow-lg">
          {REACTIONS.map((r) => (
            <button key={r.key} title={r.label} onClick={() => { onReact(r.key); setOpen(false); }}
              className="rounded-full px-1.5 text-lg transition-transform hover:scale-125" data-testid={`talk-react-${r.key}`}>{r.e}</button>
          ))}
        </div>
      )}
      {total > 0 && (
        <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
          {Object.entries(reactions).filter(([, n]) => n > 0).slice(0, 4).map(([k]) => <span key={k}>{EMOJI[k]}</span>)}
          <span className="ml-1">{total}</span>
        </span>
      )}
    </div>
  );
}

function Comments({ postId, accent, user, onCountChange }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const load = () => api.get(`/talk/posts/${postId}/comments`).then((r) => setItems(r.data.comments)).catch(() => setItems([]));
  useEffect(() => { load(); }, [postId]);
  const submit = async (parent_id = null) => {
    if (!text.trim()) return;
    try { await api.post(`/talk/posts/${postId}/comments`, { body: text, parent_id }); setText(""); onCountChange?.(1); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const react = async (cid, emoji) => { await api.post(`/talk/comments/${cid}/react`, { emoji }); load(); };
  const del = async (cid) => { await api.delete(`/talk/comments/${cid}`); onCountChange?.(-1); load(); };
  const roots = (items || []).filter((c) => !c.parent_id);
  const kids = (id) => (items || []).filter((c) => c.parent_id === id);
  const Row = ({ c, child }) => (
    <div className={`flex gap-2 ${child ? "ml-9" : ""}`} data-testid="talk-comment">
      <Avatar className="h-7 w-7 shrink-0"><AvatarImage src={fileUrl(c.author_avatar)} /><AvatarFallback className="text-[10px]">{(c.author_name || "M")[0]}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl bg-muted/60 px-3 py-2">
          <p className="text-xs font-semibold">{c.author_name} <span className="font-normal text-muted-foreground">· {c.author_badge}</span></p>
          <p className="whitespace-pre-wrap break-words text-sm">{c.body}</p>
        </div>
        <div className="mt-0.5 flex items-center gap-3 pl-2 text-[11px] text-muted-foreground">
          <span>{timeAgo(c.created_at)}</span>
          {user && REACTIONS.slice(0, 3).map((r) => <button key={r.key} onClick={() => react(c.id, r.key)} className="hover:opacity-70">{r.e}</button>)}
          {Object.values(c.reactions || {}).reduce((a, b) => a + b, 0) > 0 && <span>{Object.values(c.reactions).reduce((a, b) => a + b, 0)}</span>}
          {c.is_author && <button onClick={() => del(c.id)} className="hover:text-destructive">Delete</button>}
        </div>
      </div>
    </div>
  );
  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      {roots.map((c) => (<div key={c.id} className="space-y-2"><Row c={c} />{kids(c.id).map((k) => <Row key={k.id} c={k} child />)}</div>))}
      {user ? (
        <div className="flex items-center gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…" className="rounded-full"
            onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="talk-comment-input" />
          <Button size="icon" className="rounded-full shrink-0" onClick={() => submit()} data-testid="talk-comment-send"><PaperPlaneTilt size={16} /></Button>
        </div>
      ) : <p className="text-xs text-muted-foreground">Sign in to join the conversation.</p>}
    </div>
  );
}

function PostCard({ post, accent, user, onChange, canModerate }) {
  const [openComments, setOpenComments] = useState(false);
  const [reactions, setReactions] = useState(post.reactions || {});
  const [mine, setMine] = useState(post.my_reaction);
  const [cc, setCC] = useState(post.comment_count || 0);
  const react = async (emoji) => {
    if (!user) return toast.error("Sign in to react");
    const { data } = await api.post(`/talk/posts/${post.id}/react`, { emoji });
    setReactions(data.reactions); setMine(data.my_reaction);
  };
  const report = async () => { await api.post(`/talk/posts/${post.id}/report`, { reason: "other" }); toast.success("Reported — thanks, our team will review it"); };
  const del = async () => { if (window.confirm("Delete this post?")) { await api.delete(`/talk/posts/${post.id}`); onChange?.(); } };
  const [tr, setTr] = useState(null);
  const [showOrig, setShowOrig] = useState(false);
  const [tbusy, setTbusy] = useState(false);
  const translate = async (lang) => {
    setTbusy(true);
    try {
      const calls = [api.post("/talk/translate", { text: post.body || "", target: lang })];
      if (post.title) calls.unshift(api.post("/talk/translate", { text: post.title, target: lang }));
      const res = await Promise.all(calls);
      setTr({ lang, title: post.title ? res[0].data.text : "", body: res[res.length - 1].data.text });
      setShowOrig(false);
    } catch { toast.error("Translation failed"); } finally { setTbusy(false); }
  };
  const dispTitle = tr && !showOrig ? tr.title : post.title;
  const dispBody = tr && !showOrig ? tr.body : post.body;
  const pin = async () => { try { const { data: r } = await api.post(`/talk/posts/${post.id}/pin`); toast.success(r.pinned ? "Pinned as official" : "Unpinned"); onChange?.(); } catch { toast.error("Could not pin"); } };
  const ff = post.find_fields;
  return (
    <div className={`rounded-2xl bg-background p-4 shadow-sm ${post.pinned ? `border-2 ${accent.border} ring-2 ${accent.ring}` : "border border-border/60"}`} data-testid="talk-post">
      {post.pinned && <div className={`-mt-1 mb-2 inline-flex items-center gap-1 rounded-full ${accent.chip} px-2 py-0.5 text-[11px] font-semibold`}><PushPin size={12} weight="fill" /> Pinned · Official</div>}
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0"><AvatarImage src={fileUrl(post.author_avatar)} /><AvatarFallback>{(post.author_name || "M")[0]}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-semibold leading-tight">
            {post.author_name}
            {post.author_role === "admin" && <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-white"><SealCheck size={11} weight="fill" /> AlumniPulse</span>}
            {post.author_role === "institution" && <span className={`inline-flex items-center gap-0.5 rounded-full ${accent.chip} px-1.5 py-0.5 text-[10px] font-medium`}><SealCheck size={11} weight="fill" /> Official</span>}
          </p>
          <p className="text-xs text-muted-foreground">{post.author_badge} · {timeAgo(post.created_at)}{post.edited ? " · edited" : ""}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${accent.chip}`}>{CAT_LABEL[post.category] || post.category}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button className="text-muted-foreground hover:text-foreground" data-testid="talk-post-menu"><DotsThree size={20} /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canModerate && <DropdownMenuItem onClick={pin} data-testid="talk-pin"><PushPin size={15} className="mr-2" />{post.pinned ? "Unpin" : "Pin as official"}</DropdownMenuItem>}
            {post.is_author && <DropdownMenuItem onClick={del} className="text-destructive"><Trash size={15} className="mr-2" />Delete</DropdownMenuItem>}
            {user && !post.is_author && <DropdownMenuItem onClick={report}><Flag size={15} className="mr-2" />Report</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {dispTitle && <h4 className="mt-2 font-heading text-base font-semibold">{dispTitle}</h4>}
      {ff && (
        <div className={`mt-2 rounded-xl ${accent.soft} p-3 text-sm`}>
          <p className={`mb-1 text-xs font-semibold uppercase ${accent.text}`}>Looking for a classmate</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            {["name", "nickname", "course", "batch", "years", "last_city"].filter((k) => ff[k]).map((k) => (
              <p key={k} className="text-xs"><span className="text-muted-foreground capitalize">{k.replace("_", " ")}: </span>{ff[k]}</p>
            ))}
          </div>
        </div>
      )}
      {post.body && <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed">{dispBody}</p>}
      {post.media?.length > 0 && (
        <div className={`mt-2 grid gap-2 ${post.media.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {post.media.map((m) => <img key={m} src={fileUrl(m)} alt="" className="max-h-96 w-full rounded-xl object-cover" loading="lazy" />)}
        </div>
      )}
      {post.link && <a href={post.link} target="_blank" rel="noreferrer" className={`mt-2 inline-flex items-center gap-1 text-sm ${accent.text} hover:underline`}><LinkIcon size={14} />{post.link}</a>}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Reactions reactions={reactions} mine={mine} onReact={react} accent={accent} />
        <button onClick={() => setOpenComments((v) => !v)} className="inline-flex items-center gap-1 rounded-full border border-border/60 px-3 py-1 text-sm hover:bg-accent" data-testid="talk-comment-toggle">
          <ChatsCircle size={15} /> {cc}
        </button>
        {tr ? (
          <button onClick={() => setShowOrig((v) => !v)} className={`inline-flex items-center gap-1 rounded-full border border-border/60 px-3 py-1 text-sm hover:bg-accent`} data-testid="talk-show-original">
            <ArrowUUpLeft size={15} /> {showOrig ? `See ${tr.lang}` : "See original"}
          </button>
        ) : (
          <TranslateMenu onPick={translate} busy={tbusy} />
        )}
      </div>
      {openComments && <Comments postId={post.id} accent={accent} user={user} onCountChange={(d) => setCC((c) => Math.max(0, c + d))} />}
    </div>
  );
}

function Composer({ entityType, entityId, accent, onPosted }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("memory");
  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [link, setLink] = useState("");
  const [media, setMedia] = useState([]);
  const [ff, setFf] = useState({});
  const [busy, setBusy] = useState(false);
  const { upload, busy: uploading } = useUpload();
  const fileRef = useRef();

  if (!user) return (
    <div className={`rounded-2xl border ${accent.border} ${accent.soft} p-4 text-sm`}>
      <span className="text-muted-foreground">Sign in to share a memory, ask a question or find a classmate.</span>
    </div>
  );

  const pick = async (e) => { const ids = await upload([...e.target.files]); setMedia((m) => [...m, ...ids]); };
  const submit = async () => {
    if (!bodyText.trim() && media.length === 0 && category !== "find_colleague") return toast.error("Write something first");
    setBusy(true);
    try {
      await api.post("/talk/posts", {
        entity_type: entityType, entity_id: entityId, category, title, body: bodyText, link, media,
        find_fields: category === "find_colleague" ? ff : null,
      });
      setTitle(""); setBodyText(""); setLink(""); setMedia([]); setFf({}); setOpen(false); setCategory("memory");
      toast.success("Posted to the community");
      onPosted?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border ${accent.border} bg-background p-4 shadow-sm`}>
      {!open ? (
        <button onClick={() => setOpen(true)} className="flex w-full items-center gap-3 text-left" data-testid="talk-open-composer">
          <Avatar className="h-9 w-9"><AvatarImage src={fileUrl(user.avatar_file_id)} /><AvatarFallback>{(user.name || "M")[0]}</AvatarFallback></Avatar>
          <span className={`flex-1 rounded-full ${accent.soft} px-4 py-2 text-sm text-muted-foreground`}>Share a memory, ask a question, find a classmate…</span>
          <Sparkle className={accent.text} size={20} weight="fill" />
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {CATS.filter((c) => c.k !== "all").map((c) => (
              <button key={c.k} onClick={() => setCategory(c.k)} data-testid={`talk-cat-${c.k}`}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${category === c.k ? accent.tab : "bg-muted hover:bg-accent"}`}>{c.label}</button>
            ))}
          </div>
          {category === "find_colleague" ? (
            <div className="grid grid-cols-2 gap-2">
              {[["name", "Full name"], ["nickname", "Nickname"], ["course", "Course"], ["batch", "Batch / year"], ["years", "Approx. years"], ["last_city", "Last known city"]].map(([k, ph]) => (
                <Input key={k} value={ff[k] || ""} onChange={(e) => setFf({ ...ff, [k]: e.target.value })} placeholder={ph} data-testid={`talk-ff-${k}`} />
              ))}
            </div>
          ) : (
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a title (optional)" data-testid="talk-title" />
          )}
          <Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={3}
            placeholder={category === "find_colleague" ? "Share a message for the community…" : "What's on your mind?"} data-testid="talk-body" />
          {media.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {media.map((m) => (
                <div key={m} className="relative"><img src={fileUrl(m)} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  <button onClick={() => setMedia(media.filter((x) => x !== m))} className="absolute -right-1 -top-1 rounded-full bg-background p-0.5 shadow"><X size={12} /></button></div>
              ))}
            </div>
          )}
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Add a link (optional)" />
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pick} />
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="talk-add-photo"><ImageSquare size={16} /> {uploading ? "Uploading…" : "Photo"}</Button>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" className="rounded-full gap-1.5" onClick={submit} disabled={busy} data-testid="talk-submit"><PaperPlaneTilt size={16} /> {busy ? "Posting…" : "Post"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TalkVibes({ entityType, entityId, title, subtitle, accent = "institution", memberHint }) {
  const { user } = useAuth();
  const theme = ACCENTS[accent] || ACCENTS.institution;
  const [cat, setCat] = useState("all");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get(`/talk/${entityType}/${encodeURIComponent(entityId)}`, { params: { category: cat, limit: 30 } })
    .then((r) => setData(r.data)).catch(() => setData({ posts: [], total: 0 }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId, cat]);

  const posts = (data?.posts || []).filter((p) => {
    if (!q.trim()) return true;
    const s = `${p.title} ${p.body} ${p.author_name}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  return (
    <section className={`rounded-3xl border ${theme.border} ${theme.soft} p-5 sm:p-6 ring-1 ${theme.ring}`} id={`talk-${entityType}`} data-testid={`talk-vibes-${entityType}`}>
      <div className={`mb-4 flex flex-wrap items-center gap-3`}>
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${theme.bar} text-white shadow`}><ChatsCircle size={24} weight="duotone" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-semibold leading-tight" data-testid="talk-title-heading">{title} <span className={theme.text}>Talk – Vibes</span></h2>
          <p className="text-xs text-muted-foreground">{subtitle || "Reconnect, share memories, ask questions & find old friends."} · {data?.total ?? 0} posts{memberHint ? ` · ${memberHint}` : ""}{data?.followers ? ` · ${data.followers} following` : ""}</p>
        </div>
        {user && (
          <Button variant={data?.following ? "secondary" : "outline"} size="sm" className="rounded-full gap-1.5" data-testid="talk-follow-btn"
            onClick={async () => { try { const { data: r } = await api.post(`/talk/${entityType}/${encodeURIComponent(entityId)}/follow`); setData((d) => ({ ...d, following: r.following, followers: r.followers })); toast.success(r.following ? "Following — we'll notify you of new posts & reunions" : "Unfollowed"); } catch { toast.error("Sign in to follow"); } }}>
            {data?.following ? <><BellRinging size={15} weight="fill" /> Following</> : <><Bell size={15} /> Follow</>}
          </Button>
        )}
      </div>

      <Composer entityType={entityType} entityId={entityId} accent={theme} onPosted={load} />

      <div className="my-4 flex items-center gap-2">
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-1.5">
            {CATS.map((c) => (
              <button key={c.k} onClick={() => setCat(c.k)} data-testid={`talk-tab-${c.k}`}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${cat === c.k ? theme.tab : "bg-background border border-border/60 hover:bg-accent"}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <div className="relative hidden sm:block">
          <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="h-8 w-36 rounded-full pl-8 text-xs" data-testid="talk-search" />
        </div>
      </div>

      <div className="space-y-3">
        {!data ? <p className="py-8 text-center text-sm text-muted-foreground">Loading conversations…</p>
          : posts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/60 bg-background/60 py-10 text-center">
              <ChatsCircle className={`mx-auto mb-2 ${theme.text}`} size={30} />
              <p className="text-sm font-medium">Be the first to start the conversation</p>
              <p className="text-xs text-muted-foreground">Share a memory, ask a question, or look for a classmate.</p>
            </div>
          ) : posts.map((p) => <PostCard key={p.id} post={p} accent={theme} user={user} onChange={load} canModerate={data?.can_moderate} />)}
      </div>
    </section>
  );
}
